import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ScrollAnimation from './ScrollAnimation';
import ShareButtons from './ShareButtons';
import { categorias, proyectos } from '../data/proyectos';
import { useSeo } from '../hooks/useSeo';

const PortfolioPage: React.FC = () => {
    const [categoria, setCategoria] = useState('Todos');

    useSeo({
        title: 'Portafolio — Iron Five',
        description:
            'Sitios, plataformas y sistemas a medida que construimos y mantenemos en producción. ' +
            'Cada trabajo, contado por dentro.',
        image: '/images/casos/fundacion-hero.jpg',
        path: '/portafolio',
    });

    const filtrados =
        categoria === 'Todos' ? proyectos : proyectos.filter((p) => p.categoria === categoria);

    return (
        <div className="min-h-screen bg-gray-50 pt-24">
            <div className="container mx-auto px-6 py-12">
                <div className="text-center mb-12">
                    <ScrollAnimation>
                        <h1 className="text-4xl font-bold font-poppins text-grafito mb-4">
                            Nuestro portafolio
                        </h1>
                    </ScrollAnimation>
                    <ScrollAnimation delay={0.2}>
                        <p className="text-xl text-gray-600 font-lato max-w-2xl mx-auto">
                            No solo entregamos: acompañamos. Estos son los trabajos que construimos y que
                            hoy siguen andando en producción. Entrá a cualquiera para ver cómo fue.
                        </p>
                    </ScrollAnimation>
                </div>

                {/* Filtros. Antes estos botones estaban `disabled` y además iteraban sobre la
                    lista de trabajos en vez de la de categorías, así que había un botón por
                    proyecto y ninguno filtraba nada. */}
                <ScrollAnimation delay={0.3}>
                    <div className="flex flex-wrap justify-center gap-3 mb-12">
                        {categorias.map((c) => (
                            <button
                                key={c}
                                type="button"
                                onClick={() => setCategoria(c)}
                                aria-pressed={categoria === c}
                                className={`px-5 py-2 rounded-full font-montserrat text-sm font-medium transition-all ${
                                    categoria === c
                                        ? 'bg-cyan text-white shadow-md'
                                        : 'bg-white text-grafito hover:bg-gray-100 shadow-sm'
                                }`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </ScrollAnimation>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {filtrados.map((p, index) => (
                        <ScrollAnimation key={p.slug} delay={0.1 + index * 0.1}>
                            <article className="h-full flex flex-col bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                                <Link to={`/portafolio/${p.slug}`} className="block group">
                                    <div className="h-56 bg-gray-200 relative overflow-hidden">
                                        <img
                                            src={p.imagen}
                                            alt={p.titulo}
                                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                        />
                                        <div className="absolute top-4 right-4">
                                            <span className="px-3 py-1 bg-cyan text-white text-xs font-montserrat font-semibold rounded-full">
                                                {p.categoria}
                                            </span>
                                        </div>
                                    </div>
                                </Link>

                                <div className="p-6 flex flex-col flex-grow">
                                    <div className="flex justify-between items-start mb-3 gap-2">
                                        <h2 className="text-xl font-bold font-montserrat text-grafito flex-1">
                                            <Link
                                                to={`/portafolio/${p.slug}`}
                                                className="hover:text-cyan transition-colors"
                                            >
                                                {p.titulo}
                                            </Link>
                                        </h2>
                                        <span className="text-sm text-gray-500 font-lato whitespace-nowrap">
                                            {p.anio}
                                        </span>
                                    </div>

                                    <p className="text-gray-600 font-lato mb-4">{p.resumen}</p>

                                    {p.cliente && (
                                        <p className="text-sm font-montserrat text-grafito mb-4">
                                            Cliente:{' '}
                                            <span className="font-normal text-gray-600">{p.cliente}</span>
                                        </p>
                                    )}

                                    <div className="flex flex-wrap gap-2 mb-5">
                                        {p.tecnologias.slice(0, 3).map((t) => (
                                            <span
                                                key={t}
                                                className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-montserrat rounded"
                                            >
                                                {t}
                                            </span>
                                        ))}
                                        {p.tecnologias.length > 3 && (
                                            <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs font-montserrat rounded">
                                                +{p.tecnologias.length - 3}
                                            </span>
                                        )}
                                    </div>

                                    {/* mt-auto: las cards de una fila tienen alturas distintas y el
                                        pie tiene que quedar alineado igual. */}
                                    <div className="mt-auto pt-4 border-t border-gray-100 flex items-center justify-between gap-3">
                                        <Link
                                            to={`/portafolio/${p.slug}`}
                                            className="inline-flex items-center text-sm font-montserrat font-semibold text-cyan hover:underline"
                                        >
                                            Ver el caso
                                            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                            </svg>
                                        </Link>

                                        {/* URL de producción fija y no `window.location`:
                                            lo que se comparte tiene que abrir en el sitio
                                            público aunque el link se copie desde el build
                                            local. La barra final es a propósito — es la
                                            que sirve el index.html con los meta tags de
                                            este proyecto (ver scripts/generate-og-pages.js). */}
                                        <ShareButtons
                                            url={`https://www.ironfive.ar/portafolio/${p.slug}/`}
                                            titulo={`${p.titulo} — Iron Five`}
                                        />
                                    </div>
                                </div>
                            </article>
                        </ScrollAnimation>
                    ))}
                </div>

                <ScrollAnimation>
                    <div className="text-center mt-12">
                        <Link
                            to="/"
                            className="inline-flex items-center px-6 py-3 bg-grafito text-white font-montserrat font-semibold rounded-lg hover:bg-gray-800 transition-colors"
                        >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                            </svg>
                            Volver al inicio
                        </Link>
                    </div>
                </ScrollAnimation>
            </div>
        </div>
    );
};

export default PortfolioPage;
